/**
 * API Error Handling Utilities
 *
 * Provides consistent error parsing and type-safe error handling for API requests.
 */

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: Array<{
    field?: string;
    message: string;
  }>;
}

/**
 * Parse an unknown error into a structured ApiError
 *
 * Handles various error formats from the backend:
 * - Fetch API errors
 * - Axios-style errors
 * - Backend AppError format
 * - Network errors
 * - Generic thrown errors
 *
 * @param error - The error to parse (from catch block)
 * @returns Structured ApiError with message and optional details
 *
 * @example
 * try {
 *   await api.post('/endpoint', data);
 * } catch (err) {
 *   const apiError = parseApiError(err);
 *   toast.error(apiError.message);
 *   if (apiError.details) {
 *     // Show field-level validation errors
 *   }
 * }
 */
export function parseApiError(error: unknown): ApiError {
  // Default fallback error
  const defaultError: ApiError = {
    message: 'An unexpected error occurred. Please try again.',
    code: 'UNKNOWN_ERROR',
  };

  if (!error) {
    return defaultError;
  }

  // Handle Response objects (from fetch API)
  if (error instanceof Response) {
    return {
      message: error.statusText || 'Request failed',
      status: error.status,
      code: `HTTP_${error.status}`,
    };
  }

  // Handle Error objects
  if (error instanceof Error) {
    // Check if it's a network error
    if (error.message.includes('fetch') || error.message.includes('network')) {
      return {
        message: 'Network error. Please check your connection and try again.',
        code: 'NETWORK_ERROR',
      };
    }

    // Try to parse as Axios-style error format
    interface AxiosLikeError extends Error {
      response?: {
        status: number;
        data?: {
          error?: { message?: string; code?: string; details?: ApiError['details'] };
          message?: string;
          code?: string;
        } | string;
      };
      code?: string;
      status?: number;
      details?: ApiError['details'];
    }

    const axiosErr = error as AxiosLikeError;

    if (axiosErr.response?.data && typeof axiosErr.response.data === 'object') {
      const data = axiosErr.response.data;

      // Backend AppError format (from error-handler.ts)
      if (data.error?.message) {
        return {
          message: data.error.message || defaultError.message,
          code: data.error.code,
          status: axiosErr.response.status,
          details: data.error.details,
        };
      }

      if (data.message) {
        return {
          message: data.message,
          code: data.code,
          status: axiosErr.response.status,
        };
      }
    }

    // If response data is a string
    if (axiosErr.response?.data && typeof axiosErr.response.data === 'string') {
      return {
        message: axiosErr.response.data,
        status: axiosErr.response.status,
      };
    }

    // Plain Error with structured fields attached (e.g. errors re-thrown
    // through useTenantMutation, which preserves `status`/`code`/`details`
    // so per-field validation errors survive the mutation boundary).
    return {
      message: error.message,
      code: axiosErr.code,
      status: axiosErr.status,
      details: Array.isArray(axiosErr.details) ? axiosErr.details : undefined,
    };
  }

  // Handle plain objects with error properties
  if (typeof error === 'object' && error !== null) {
    const obj = error as Record<string, unknown>;

    if (typeof obj.message === 'string') {
      return {
        message: obj.message,
        code: typeof obj.code === 'string' ? obj.code : undefined,
        status: typeof obj.status === 'number' ? obj.status : undefined,
        details: Array.isArray(obj.details) ? obj.details as ApiError['details'] : undefined,
      };
    }

    if (typeof obj.error === 'object' && obj.error !== null) {
      const nested = obj.error as Record<string, unknown>;
      if (typeof nested.message === 'string') {
        return {
          message: nested.message,
          code: typeof nested.code === 'string' ? nested.code : undefined,
          status: typeof obj.status === 'number' ? obj.status : undefined,
          details: Array.isArray(nested.details) ? nested.details as ApiError['details'] : undefined,
        };
      }
    }
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      message: error,
    };
  }

  return defaultError;
}

/** Longer than this and a toast is a wall of text nobody reads. */
const MAX_DETAILS_SHOWN = 3;

/**
 * `needed_by` -> "Needed by"; `items.0.quantity` -> "Items > Quantity".
 *
 * The backend names the field in its own schema vocabulary. That is precise and
 * unreadable; the operator is looking at a form, not a Zod schema. Array
 * indices are dropped -- "Items > 0 > Quantity" tells nobody anything, and the
 * message that follows usually identifies the row.
 */
function humanizeField(path: string): string {
  const words = path
    .split('.')
    .filter((segment) => segment !== '' && !/^\d+$/.test(segment))
    .map((segment) => segment.replace(/_/g, ' '))
    .join(' > ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Get a user-friendly error message from an ApiError.
 *
 * DETAILS ARE INCLUDED BY DEFAULT, and that is the point of this function.
 *
 * The backend already works out exactly which field was wrong and why -- the
 * Zod branch of `error-handler.ts` sends `details: [{ field, message }]` with
 * every 400. `includeDetails` used to default to FALSE, and only 3 of the 236
 * call sites passed true -- `useTenantMutation`'s error toast, the path almost
 * every form in the app reports through, was not one of them. So very nearly
 * every validation failure said:
 *
 *     Invalid request data
 *
 * and threw away the part that tells you what to fix. A form that refuses
 * without saying why is a form an operator abandons -- SupplyRequestModal
 * carries a comment about exactly that costing a whole supply request, with
 * "blank the field and try again" as the only discoverable workaround.
 *
 * Nothing changes for errors that carry no details (404s, conflicts, network
 * failures): those still return their own message untouched.
 *
 * @param error - The ApiError to format
 * @param includeDetails - Pass false only where the caller renders the fields itself
 * @returns Formatted error message string
 *
 * @example
 * getErrorMessage(parseApiError(err));
 * // "Needed by: Invalid datetime"          (was: "Invalid request data")
 */
export function getErrorMessage(error: ApiError, includeDetails = true): string {
  const details = includeDetails ? error.details ?? [] : [];
  if (details.length === 0) return error.message;

  const shown = details
    .slice(0, MAX_DETAILS_SHOWN)
    .map((detail) => (detail.field ? `${humanizeField(detail.field)}: ${detail.message}` : detail.message));
  const remaining = details.length - shown.length;
  if (remaining > 0) shown.push(`and ${remaining} more`);

  const listed = shown.join('; ');

  /*
   * "Invalid request data" carries nothing once the fields are named, so the
   * named fields replace it. Any other message (a business-rule AppError that
   * also supplied details) still leads, because it says something the field
   * list does not.
   */
  return error.code === 'VALIDATION_ERROR' ? listed : `${error.message} - ${listed}`;
}

/**
 * Check if an error is a specific HTTP status code
 *
 * @param error - The error to check
 * @param status - The HTTP status code to match
 * @returns True if the error has the specified status code
 *
 * @example
 * if (isErrorStatus(error, 404)) {
 *   toast.error('Resource not found');
 * } else if (isErrorStatus(error, 403)) {
 *   toast.error('You do not have permission to perform this action');
 * }
 */
export function isErrorStatus(error: unknown, status: number): boolean {
  const apiError = parseApiError(error);
  return apiError.status === status;
}

/**
 * Check if an error is a validation error (400 status)
 *
 * @param error - The error to check
 * @returns True if the error is a validation error
 */
export function isValidationError(error: unknown): boolean {
  return isErrorStatus(error, 400);
}

/**
 * Check if an error is an authentication error (401 status)
 *
 * @param error - The error to check
 * @returns True if the error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  return isErrorStatus(error, 401);
}

/**
 * Check if an error is a permission error (403 status)
 *
 * @param error - The error to check
 * @returns True if the error is a permission error
 */
export function isPermissionError(error: unknown): boolean {
  return isErrorStatus(error, 403);
}

/**
 * Check if an error is a not found error (404 status)
 *
 * @param error - The error to check
 * @returns True if the error is a not found error
 */
export function isNotFoundError(error: unknown): boolean {
  return isErrorStatus(error, 404);
}

/**
 * Check if an error is a conflict error (409 status)
 *
 * @param error - The error to check
 * @returns True if the error is a conflict error
 */
export function isConflictError(error: unknown): boolean {
  return isErrorStatus(error, 409);
}

/**
 * Check if an error is a network error
 *
 * @param error - The error to check
 * @returns True if the error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  const apiError = parseApiError(error);
  return apiError.code === 'NETWORK_ERROR';
}

/**
 * Auth outcomes the backend declares FINAL for the credential in use
 * (backend `TERMINAL_AUTH_CODES`, common/middleware/error-handler.ts):
 *
 *   SESSION_REVOKED     this device's session was revoked (lost phone, admin)
 *   MEMBERSHIP_REMOVED  the user was removed from the organization
 *   USER_DISABLED       the account was disabled
 *   TOKEN_REVOKED       signed out elsewhere / password changed
 *   DEVICE_REPORTED_LOST this device was reported lost or stolen
 *
 * Every response carrying one also carries `error.terminal: true`.
 */
export const TERMINAL_AUTH_ERROR_CODES = [
  'SESSION_REVOKED',
  'MEMBERSHIP_REMOVED',
  'USER_DISABLED',
  'TOKEN_REVOKED',
  'DEVICE_REPORTED_LOST',
] as const;

/**
 * True only when the server says this credential is dead: stop retrying,
 * re-authenticate (and, on a field device, keep the queued uploads).
 *
 * Field clients must treat every OTHER failure — including a plain 401, 403,
 * 429 or the 503 AUTH_REVOCATION_CHECK_FAILED a DB blip produces — as
 * transient and retry with backoff, so a dead zone or a server hiccup never
 * costs a responder their session. Branch on this, not on the status code.
 */
export function isTerminalAuthError(error: unknown): boolean {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data;
  if (data && typeof data === 'object') {
    const body = (data as { error?: { terminal?: unknown } }).error;
    if (body && body.terminal === true) return true;
  }
  const { code } = parseApiError(error);
  return code !== undefined && (TERMINAL_AUTH_ERROR_CODES as readonly string[]).includes(code);
}
